import React, { useState, useEffect } from 'react';
import { offersService } from '../../../../services/offersService';
import { toast } from 'react-hot-toast';
import './OfferModal.css';

const OfferModal = ({ request, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    price: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Offer price system states
  const [offerPrice, setOfferPrice] = useState(0);
  const [systemActive, setSystemActive] = useState(false);
  const [priceLoading, setPriceLoading] = useState(true);
  const [systemMessage, setSystemMessage] = useState('');

  // Add modal-open class to body when modal opens, but use modal-open-aside if sidebar is open
  useEffect(() => {
    // Check if sidebar modal is open (mobile menu)
    const sidebarOpen = document.getElementById('aside')?.classList.contains('in');
    
    if (sidebarOpen) {
      document.body.classList.add('modal-open-aside');
    } else {
      document.body.classList.add('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
      document.body.classList.remove('modal-open-aside');
    };
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Fetch offer price on component mount
  useEffect(() => {
    fetchOfferPrice();
  }, []);

  const fetchOfferPrice = async () => {
    setPriceLoading(true);
    try {
      const response = await offersService.getOfferPrice();
      console.log('💰 Offer price data:', response); // Debug log
      setOfferPrice(response.offer_price || 0);
      setSystemActive(response.is_active || false);
      setSystemMessage(response.message || '');
    } catch (error) {
      console.error('Offer price fetch error:', error);
      // Default values on error
      setOfferPrice(0);
      setSystemActive(false);
      setSystemMessage('Teklif ücreti bilgisi alınamadı');
    } finally {
      setPriceLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Price validation
    const price = parseFloat(formData.price);
    if (!formData.price) {
      newErrors.price = 'Fiyat zorunludur';
    } else if (isNaN(price) || price < 100) {
      newErrors.price = 'Fiyat minimum 100 TL olmalıdır';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Mesaj zorunludur';
    } else if (formData.message.trim().length < 50) {
      newErrors.message = 'Mesaj minimum 50 karakter olmalıdır';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        musician_request: request.id,
        price: formData.price,
        message: formData.message.trim()
      };

      console.log('Submitting offer with data:', submitData); // Debug log
      const response = await offersService.createOffer(submitData);
      console.log('Offer submission response:', response); // Debug log
      
      if (response.status === 201) {
        toast.success('Teklifiniz başarıyla gönderildi!');
        onSuccess();
      } else {
        console.error('Unexpected response status:', response.status);
        toast.error('Teklif gönderilirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Offer submission error:', error);
      if (error?.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Teklif gönderilirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    // Only close if clicked on the backdrop itself, not on child elements
    if (e.target === e.currentTarget && 
        (e.target.classList.contains('modal-backdrop') || 
         e.target.classList.contains('offer-modal'))) {
      onClose();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  return (
    <>
      <div className="modal-backdrop fade in offer-modal-backdrop" onClick={handleBackdropClick}></div>
      <div className="modal fade in offer-modal" style={{ display: 'block' }} onClick={handleBackdropClick}>
        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Teklif Ver</h4>
              <button 
                type="button" 
                className="close" 
                onClick={onClose}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-body scrollable">
              {/* Offer Price Card */}
              {priceLoading ? (
                <div className="text-center m-b">
                  <i className="fa fa-spinner fa-spin"></i> Ücret bilgisi yükleniyor...
                </div>
              ) : !systemActive ? (
                <div className="alert alert-danger m-b">
                  <i className="fa fa-exclamation-triangle m-r-xs"></i>
                  <strong>Sistem Kapalı:</strong> {systemMessage || 'Teklif sistemi şu anda aktif değil'}
                </div>
              ) : (
                <div className="box success m-b">
                  <div className="box-body text-center">
                    <div className="text-2x _600 m-b-xs">
                      <i className="fa fa-credit-card m-r-xs"></i>
                      {formatPrice(offerPrice)}
                    </div>
                    <div className="text-sm text-muted">
                      Her teklif için tek seferlik ücret
                    </div>
                  </div>
                </div>
              )}

              {/* Request Details */}
              <div className="box m-b">
                <div className="box-header">
                  <h5>Talep Detayları</h5>
                </div>
                <div className="box-body">
                  <div className="row">
                    <div className="col-sm-6">
                      <strong>Organizasyon Tipi:</strong>
                      <div className="m-b-sm">
                        <span className="label label-primary">{request.organization_type}</span>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <strong>Müzisyen Tipi:</strong>
                      <div className="m-b-sm">
                        <span className="label label-info">{request.musician_type}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="m-b-sm">
                    <strong>Konum:</strong> {request.city_name} / {request.town_name}
                  </div>
                  
                  <div className="m-b-sm">
                    <strong>Talep Sahibi:</strong> {request.requester_name}
                  </div>
                  
                  <div className="m-b-sm">
                    <strong>Talep Tarihi:</strong> {formatDate(request.create_time)}
                  </div>
                  
                  <div className="m-b-sm">
                    <strong>Açıklama:</strong>
                    <p className="text-sm">{request.description}</p>
                  </div>
                  
                  <div className="m-b-sm">
                    <strong>Diğer Teklifler:</strong> {request.offers_count} teklif
                  </div>
                </div>
              </div>

              {/* Offer Form */}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="price">Teklif Fiyatı (TL) *</label>
                  <input
                    id="price"
                    type="number"
                    min="100"
                    step="0.01"
                    className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                    placeholder="Örn: 1500"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    disabled={loading || !systemActive}
                  />
                  {errors.price && (
                    <div className="text-danger m-t-xs">
                      <small>{errors.price}</small>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Mesaj *</label>
                  <textarea
                    id="message"
                    className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                    rows="4"
                    placeholder="Merhaba, etkinliğiniz için teklif vermek istiyorum..."
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    disabled={loading || !systemActive}
                  />
                  <small className="text-muted">
                    {formData.message.length} karakter (minimum 50 karakter)
                  </small>
                  {errors.message && (
                    <div className="text-danger m-t-xs">
                      <small>{errors.message}</small>
                    </div>
                  )}
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-default" 
                onClick={onClose}
                disabled={loading}
              >
                İptal
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleSubmit}
                disabled={loading || !systemActive || !formData.price || !formData.message.trim()}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin m-r-xs"></i>
                    Teklif Gönderiliyor...
                  </>
                ) : !systemActive ? (
                  <>
                    <i className="fa fa-ban m-r-xs"></i>
                    Sistem Kapalı
                  </>
                ) : (
                  <>
                    <i className="fa fa-paper-plane m-r-xs"></i>
                    Teklif Ver ({formatPrice(offerPrice)})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferModal; 